import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { addLeadMagnetSubscriber } from '../utils/kit';

const Services = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // If navigated with a hash to starter-packs (e.g., #/services#starter-packs), scroll to that section
  useEffect(() => {
    const hash = window.location.hash || '';
    if (hash.includes('starter-packs')) {
      const el = document.getElementById('starter-packs');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, []);

  // Starter Pack email form handlers (Netlify Forms)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState('Recipe'); // 'Recipe' | 'Workout'
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Absolute URL to recipe starter pack to ensure it opens correctly with HashRouter
  const recipePackUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/sample-ebooks/alera-recipe-starter-pack.html`
    : '/sample-ebooks/alera-recipe-starter-pack.html';
  // Absolute URL to workout starter pack
  const workoutPackUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/sample-ebooks/alera-workout-starter-pack.html`
    : '/sample-ebooks/alera-workout-starter-pack.html';

  const encode = (data) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const openModal = (pack) => {
    setSelectedPack(pack);
    setIsModalOpen(true);
    setSubmitted(false);
    setError('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEmail('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Add subscriber to Kit (ConvertKit) for email marketing
      await addLeadMagnetSubscriber({
        email,
        packType: selectedPack.toLowerCase(),
      });
      
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      // Still show success so user can download
      console.error('Kit subscription error:', err);
      setSubmitted(true);
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  // Removed old sample arrays (sampleRecipes, sampleWorkouts) and unused services preview data

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-rose-50 to-beige-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-playfair font-medium text-charcoal-900 mb-4">
              What Alera Has to Offer
            </h1>
            <div className="h-1 w-28 mx-auto mb-6 rounded-full" style={{ background: 'linear-gradient(90deg, #d9a79c 0%, rgba(201,163,130,0.8) 50%, #d9a79c 100%)' }}></div>
            <p className="text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive wellness solutions designed specifically for women managing allergies, 
              eczema, and food sensitivities. Transform your health with expert guidance and community support.
            </p>
          </motion.div>

          {/* What We Offer Grid */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Recipe Library */}
            <div className="bg-gradient-to-br from-white to-rose-50 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border" style={{ borderColor: '#f0e0dc' }}>
              <div className="h-40 bg-rose-100 overflow-hidden">
                <img 
                  src="/images/Alera - servicesrecipetile.jpg" 
                  alt="Allergy-friendly recipes" 
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">New Monthly Recipes!</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                Access our growing library of allergy-friendly recipes with new additions every month! Browse by dietary needs, allergies, and meal type.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• New recipes added every month</li>
                <li>• Dairy-free, gluten-free options</li>
                <li>• Anti-inflammatory focus</li>
                <li>• Filter by allergies & preferences</li>
              </ul>
              </div>
            </div>

            {/* Workout Programs */}
            <div className="bg-gradient-to-br from-white to-beige-50 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-beige-100">
              <div className="h-40 bg-beige-100 overflow-hidden">
                <img 
                  src="/images/hero-background.jpg" 
                  alt="Monthly workout programs" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Monthly Workout Programs</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                Various monthly updated fitness programs designed for all levels and lifestyles. At-home workouts with minimal equipment required.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• "Grow Your Glutes" - 3 days/week</li>
                <li>• Prenatal & postnatal programs</li>
                <li>• Corporate wellness routines</li>
                <li>• Gentle recovery sessions</li>
              </ul>
              </div>
            </div>

            {/* Health & Wellness */}
            <div className="bg-gradient-to-br from-white to-rose-50/40 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border" style={{ borderColor: '#f0e0dc' }}>
              <div className="h-40 bg-rose-100/40 overflow-hidden">
                <img 
                  src="/images/alera-healthytipsservices.jpg" 
                  alt="Health tips and wellness guides" 
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Health Tips & Guides</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                Expert guidance and practical tips to heal and manage eczema, allergies, and overall wellness naturally.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• Eczema management strategies</li>
                <li>• Natural healing approaches</li>
                <li>• Stress reduction techniques</li>
                <li>• Supplement recommendations</li>
              </ul>
              </div>
            </div>

            {/* Community */}
            <div className="bg-gradient-to-br from-white to-rose-50 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-rose-100">
              <div className="h-40 bg-rose-100 overflow-hidden">
                <img 
                  src="/images/alera-servicescommunity.jpg" 
                  alt="Community forum" 
                  loading="lazy"
                  className="w-full h-full object-cover object-bottom"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Community Forum</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                Connect with like-minded women on similar health journeys. Share experiences, ask questions, and find support.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• Private group discussions</li>
                <li>• Recipe sharing & swaps</li>
                <li>• Q&A with Kate</li>
                <li>• Success story celebrations</li>
              </ul>
              </div>
            </div>

            {/* Exclusive Resources */}
            <div className="bg-gradient-to-br from-white to-rose-50 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border" style={{ borderColor: '#f0e0dc' }}>
              <div className="h-40 bg-rose-100 overflow-hidden">
                <img 
                  src="/images/alera-servicesebook.jpg" 
                  alt="Exclusive wellness resources" 
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Exclusive Resources</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                Access to premium wellness guides, seasonal e-books, and early access to new products and programs.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• Seasonal wellness e-books</li>
                <li>• Early product access</li>
                <li>• Exclusive discounts</li>
                <li>• Monthly Zoom workshops</li>
              </ul>
              </div>
            </div>

            {/* And More */}
            <div className="bg-gradient-to-br from-rose-100 to-beige-100 rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 border-2" style={{ borderColor: '#f0e0dc' }}>
              <div className="h-40 bg-beige-200 overflow-hidden">
                <img 
                  src="/images/Alera-servicesandmore.jpg" 
                  alt="And so much more" 
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">And So Much More!</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                We're constantly adding new features and resources based on our community's needs and feedback.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2">
                <li>• Curated wellness content</li>
                <li>• Progress tracking tools</li>
                <li>• Expert guest content</li>
                <li>• Holistic lifestyle tips</li>
              </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Alera Starter Packs Section */}
      <section id="starter-packs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-charcoal-900 mb-4">
              Download Your Free Alera Starter Pack
            </h2>
            <div className="h-1 w-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-sage-300 via-rose-300 to-beige-300"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Recipe Starter Pack */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-rose-50 rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 border" style={{ borderColor: '#f0e0dc' }}
            >
              <h3 className="text-2xl font-semibold text-charcoal-900 mb-3">3-Day Recipe Reset</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                3 full days of allergy-friendly meals — breakfast, lunch & dinner — with a shopping list so you can start this week.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2 mb-6">
                <li>• 6 anti-inflammatory recipes</li>
                <li>• Complete shopping list included</li>
                <li>• Dairy-free, gluten-free & egg-free options</li>
              </ul>
              <motion.a
                href="https://alera-2.kit.com/34c752c764"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{ backgroundColor: '#d9a79c' }}
                className="block w-full text-white py-3 rounded-full font-medium hover:opacity-90 transition-all duration-200 shadow-soft hover:shadow-soft-lg text-center"
              >
                Get Your Free Pack
              </motion.a>
            </motion.div>

            {/* Workout Starter Pack */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-beige-50 rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-beige-100"
            >
              <h3 className="text-2xl font-semibold text-charcoal-900 mb-3">3-Day Workout Reset</h3>
              <p className="text-charcoal-700 leading-relaxed mb-3 text-sm">
                3 structured sessions — upper body, lower body & home HIIT — with warm-ups, cool-downs, and low-impact options.
              </p>
              <ul className="text-sm text-charcoal-600 space-y-2 mb-6">
                <li>• Gym + home workouts included</li>
                <li>• Clear form cues & rest times</li>
                <li>• Beginner to intermediate friendly</li>
              </ul>
              <motion.a
                href="https://alera-2.kit.com/4c452366a7"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{ backgroundColor: '#d9a79c' }}
                className="block w-full text-white py-3 rounded-full font-medium hover:opacity-90 transition-all duration-200 shadow-soft hover:shadow-soft-lg text-center"
              >
                Get Your Free Pack
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Email Capture Modal (Netlify Form) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md bg-white rounded-2xl shadow-soft-lg p-6 relative"
          >
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-charcoal-400 hover:text-charcoal-600"
              aria-label="Close"
            >
              ✕
            </button>
            <h3 className="text-2xl font-semibold text-charcoal-900 mb-2">Get Your Free {selectedPack} Starter Pack</h3>
            <p className="text-charcoal-600 mb-4">Enter your email and we’ll send the PDF straight to your inbox.</p>

            <form name="starter-pack-request" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit}>
              {/* Netlify required fields */}
              <input type="hidden" name="form-name" value="starter-pack-request" />
              <input type="hidden" name="packType" value={selectedPack} />
              <p className="hidden">
                <label>
                  Don’t fill this out if you’re human: <input name="bot-field" onChange={() => {}} />
                </label>
              </p>

              <label className="block text-sm font-medium text-charcoal-800 mb-2" htmlFor="starter-email">Email</label>
              <input
                id="starter-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ borderColor: '#f0e0dc' }}
                className="w-full mb-3 rounded-xl border px-4 py-3 focus:outline-none focus:ring-2"
                onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                placeholder="you@example.com"
              />

              {error && <div className="text-rose-600 text-sm mb-3">{error}</div>}
              {submitted ? (
                <div className="bg-rose-50 rounded-xl p-3 text-sm mb-3" style={{ color: '#d9a79c' }}>
                  <div className="mb-1">Thanks! Please check your email for your {selectedPack} Starter Pack.</div>
                  {selectedPack === 'Recipe' && (
                    <div>
                      Or, open it now: <a href={recipePackUrl} target="_blank" rel="noopener" style={{ color: '#d9a79c' }} className="underline hover:opacity-80">Alera Free Recipe Starter Pack</a>
                    </div>
                  )}
                  {selectedPack === 'Workout' && (
                    <div>
                      Or, open it now: <a href={workoutPackUrl} target="_blank" rel="noopener" style={{ color: '#d9a79c' }} className="underline hover:opacity-80">Alera Free Workout Starter Pack</a>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border-2 border-charcoal-200 text-charcoal-700 py-3 rounded-full font-medium hover:bg-charcoal-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ backgroundColor: '#d9a79c' }}
                  className="flex-1 text-white py-3 rounded-full font-medium hover:opacity-90 transition disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Send Your Free Pack'}
                </button>
              </div>

              <p className="text-xs text-charcoal-500 mt-3">
                You can unsubscribe anytime. We respect your privacy.
              </p>
            </form>
          </motion.div>
        </div>
      )}

      {/* Subscription Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50 to-beige-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-charcoal-900 mb-6">
              Join the Alera Community
            </h2>
            <div className="h-1 w-24 mx-auto rounded-full" style={{ background: 'linear-gradient(90deg, #d9a79c 0%, rgba(201,163,130,0.8) 50%, #d9a79c 100%)' }}></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-12 items-stretch max-w-3xl mx-auto">
            {/* Foundation Plan */}
            {/* Subscription simplified to Freedom plan only */}

            {/* Freedom Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-[28px] p-[1px]" style={{ background: 'linear-gradient(90deg, #f0e0dc 0%, #d9a79c 50%, #f0e0dc 100%)' }}>
                <div className="bg-white rounded-[27px] shadow-soft-lg p-8 border relative flex flex-col h-full" style={{ borderColor: '#f0e0dc' }}>
                  {/* Single plan - badge removed */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-semibold text-charcoal-900 mb-2">Freedom</h3>
                    <div className="mb-4">
                      <p className="text-5xl font-bold text-charcoal-900 mb-2">$25<span className="text-2xl text-charcoal-600">/month</span></p>
                      <p className="text-sm text-charcoal-500 font-nunito">Annual plans available</p>
                    </div>
                    <p className="text-charcoal-600 leading-relaxed">
                      Nourish, move, track, and thrive with confidence
                    </p>
                  </div>
                  <ul className="space-y-3 flex-1 mb-8">
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Full recipe library with filters (allergy-friendly, diet type)
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Complete workout library (glute, corporate, prenatal, etc)
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Cycle tracking with ovulation predictions & fertility insights
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Goal setting & progress tracking tools
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Journal reflections for mindfulness & self-discovery
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Exclusive wellness guides (eczema, anti-inflammatory)
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Premium community access (Q&As, recipe swaps)
                    </li>
                    <li className="flex items-center text-charcoal-600">
                      <svg style={{ color: '#d9a79c' }} className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Early access to new products + free seasonal ebooks
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <motion.button
                      onClick={() => navigate('/signup')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ backgroundColor: '#d9a79c' }}
                      className="w-full text-white py-3 rounded-full font-medium hover:opacity-90 hover:shadow-soft-lg transition-all duration-300"
                    >
                      Join Freedom
                    </motion.button>
                    <p className="text-sm text-charcoal-500 text-center mt-3 font-nunito">
                      Cancel anytime • Flexible and stress-free
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </section>


      
    </div>
  );
};

export default Services;
